import React from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import Search from 'components/shared/index/filters/search';
import Table from 'components/shared/table';
import { LabeledCheckbox } from 'components/shared/checkbox';
import ActionBarCount from 'components/archives/ActionBarCount';
import ActionBarStatus from 'components/archives/ActionBarStatus';
import BulkActionButton from 'components/shared/index/action_bar/action_button';

const titleDisplayFromType = {
  lesson: t('archive.lessons.json_header'),
  user: t('archive.users.json_header'),
  event: t('archive.events.json_header'),
  group: t('archive.groups.json_header'),
  'group::smart': t('archive.smart.json_header'),
  path: t('archive.paths.json_header'),
  'practice/container': t('archive.containers.json_header'),
};

const attemptsDisplayFromType = {
  lesson: t('archive.lessons.activity'),
  user: t('archive.users.activity'),
  event: t('archive.events.activity'),
  group: t('archive.groups.activity'),
  'group::smart': t('archive.smart.activity'),
  path: t('archive.paths.activity'),
  'practice/container': t('archive.containers.activity'),
};

const resultsDisplayFromType = {
  lesson: t('archive.lessons.results_header'),
  user: t('archive.users.results_header'),
  event: t('archive.events.results_header'),
  group: t('archive.groups.results_header'),
  'group::smart': t('archive.smart.results_header'),
  path: t('archive.paths.results_header'),
  'practice/container': t('archive.containers.results_header'),
};

const ArchiveTableHeader = ({
  archivables,
  loading,
  error,
  totalResults,
  query,
  setQuery,
  type,
  toggleArchivables,
  indeterminate,
  selectedArchivables,
  handleBulkRestore,
  bulkActionStatus,
  canUnarchive,
}) => {
  const { inProgress, success, done } = bulkActionStatus;
  const archivableType = resultsDisplayFromType[type];
  const selectedArchivablesCount = selectedArchivables.length;
  const isLoading = [loading || inProgress, !inProgress && done].some(v => !!v);
  const disableCheckbox =
    loading ||
    inProgress ||
    success ||
    (!canUnarchive && ['user', 'group', 'group::smart'].includes(type));

  const hasBulkError = success === false;
  const showActionBarStatus = isLoading || hasBulkError;
  const showActionBarCount = !isLoading && !error;
  const styleName = classNames('Index-toolbar', {
    'Index-toolbar--secondary': true,
    'Index-toolbar--success': success,
    'Index-toolbar--danger': hasBulkError,
  });

  return (
    <div>
      <CSSTransition
        classNames="Index-toolbar--secondary"
        timeout={300}
        unmountOnExit
        in
      >
        <div className={styleName}>
          <Table.BodyCell size="xs">
            {archivables.length > 0 && (
              <LabeledCheckbox
                disabled={disableCheckbox}
                onChange={toggleArchivables}
                checked={!!selectedArchivablesCount}
                indeterminate={indeterminate}
                className="u-margin-0"
                ariaLabel={t('common.select_all')}
              />
            )}
          </Table.BodyCell>

          {showActionBarStatus && (
            <ActionBarStatus
              type={archivableType}
              hasError={hasBulkError}
              bulkDone={done}
              bulkCount={selectedArchivablesCount}
              bulkInProgress={inProgress}
            />
          )}

          {showActionBarCount && (
            <ActionBarCount
              totalResults={totalResults}
              selectedCount={selectedArchivablesCount}
              type={archivableType}
            />
          )}

          <div className="Index-toolbarActions">
            {!selectedArchivablesCount && !inProgress && (
              <Search
                className="IndexFilter-search--archives"
                value={query}
                submitCallback={setQuery}
                allowEmpty
                required={false}
              />
            )}
            {!!selectedArchivablesCount && !inProgress && (
              <BulkActionButton
                label={t('actions.restore')}
                iconName="restore"
                onClick={handleBulkRestore}
                onClickKey="restore"
                modalTitle={t('archive.bulk_actions.restore_archivable', {
                  type: archivableType,
                })}
                modalMessage={t(
                  'archive.bulk_actions.restore_confirmation_message',
                  {
                    count: selectedArchivablesCount,
                    type: archivableType,
                  },
                )}
                modalConfirmButton={t('archive.bulk_actions.confirm_restore')}
              />
            )}
          </div>
        </div>
      </CSSTransition>
      {!!archivables.length && (
        <Table.Header>
          <Table.HeaderCell size="xs" />
          <Table.HeaderCell size="xl">
            {titleDisplayFromType[type]}
          </Table.HeaderCell>
          <Table.HeaderCell size="s" hideBelowMD>
            {t('archive.archived_at')}
          </Table.HeaderCell>
          <Table.HeaderCell size="s" center hideBelowMD contentClassName="jc-c">
            {attemptsDisplayFromType[type]}
          </Table.HeaderCell>
        </Table.Header>
      )}
    </div>
  );
};

export default ArchiveTableHeader;
